import React, { useEffect, useState } from "react";
import "./dealInformationForm.scss";
import { isEqual } from "lodash";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import * as api from "../../../api/generated-api";
import DKFormToggleButtonGroup from "../../common/forms/DKFormToggleButtonGroup";
import DKFormTextInput from "../../common/widgets/formVariablesWidget/DKFormTextInput";
import DKFormMoneyInput from "../../common/widgets/formVariablesWidget/DKFormMoneyInput";
import { useTheme } from "@mui/material";

type DealInformationFormProps = {
  deal: api.DealCreate;
  dealSeries: api.DealSeries[];
  dealLegalEntityInvestments: api.DealLegalEntityInvestment[];
  onDealChange?: (updatedDeal: api.DealUpdate) => void;
  onDealSeriesChange?: (updatedDealSeries: api.DealSeries[]) => void;
};

const DealInformationForm = ({
  deal,
  dealSeries = [],
  dealLegalEntityInvestments = [],
  onDealChange,
  onDealSeriesChange,
}: DealInformationFormProps) => {
  const [formDeal, setFormDeal] = useState<api.DealCreate>(null);
  const [isDealSeriesCustomName, setIsDealSeriesCustomName] = useState(false);
  const [formDealSeries, setFormDealSeries] = useState<api.DealSeries[]>(null);
  const [formDealSeriesErrorIndex, setFormDealSeriesErrorIndex] =
    useState<number>(null);
  const [isSafeConversion, setIsSafeConversion] = useState(false);

  const theme = useTheme();

  const baseSeriesNames = ["Series Seed", "Series A", "Series B", "Series C"];

  useEffect(() => {
    if (deal && isEqual(deal, formDeal) === false) {
      setFormDeal(deal);

      if (baseSeriesNames.includes(deal.seriesOfPreferredStockSold)) {
        setIsDealSeriesCustomName(false);
      } else {
        setIsDealSeriesCustomName(true);
      }
    }
  }, [deal]);

  const isSeriesDeletable = (
    series: api.DealSeries,
    investments: api.DealLegalEntityInvestment[]
  ): boolean => {
    if (series.isPrimary) return false;

    const foundInvestment = investments.find((investment) => {
      if (series.id == investment.dealSeriesId) {
        return investment;
      }
    });

    return !foundInvestment;
  };

  const isPricePerShareUpdatable = (
    series: api.DealSeries,
    investments: api.DealLegalEntityInvestment[]
  ): boolean => {
    const foundInvestmentWithWire = investments.find((investment) => {
      if (series.id == investment.dealSeriesId && investment.wireDateReceived) {
        return investment;
      }
    });

    return !foundInvestmentWithWire;
  };

  useEffect(() => {
    if (dealSeries && isEqual(dealSeries, formDealSeries) === false) {
      setFormDealSeries(dealSeries);
      setIsSafeConversion(dealSeries && dealSeries.length > 1);
    }
  }, [dealSeries, dealLegalEntityInvestments]);

  useEffect(() => {
    if (formDeal && onDealChange) {
      onDealChange({
        totalRoundSize: formDeal.totalRoundSize,
        valuation: formDeal.valuation,
        seriesOfPreferredStockSold: formDeal.seriesOfPreferredStockSold,
      });
    }
  }, [formDeal]);

  useEffect(() => {
    if (formDealSeries && onDealSeriesChange) {
      onDealSeriesChange(formDealSeries);
    }
  }, [formDealSeries]);

  const handleNumberInputChange = (property: string, value: number) => {
    setFormDeal({ ...formDeal, [property]: value });
  };

  const handleDealSeriesChange = (e) => {
    const updatedSeriesName: string = e.target.value;

    setFormDeal({
      ...formDeal,
      seriesOfPreferredStockSold: updatedSeriesName,
    });

    const previousDealSeriesName = formDeal.seriesOfPreferredStockSold;

    //update the deal series name in the deal series array
    const updatedDealSeries = [...formDealSeries];

    const updatedDealSeriesWithNewNames = updatedDealSeries.map(
      (series, index) => {
        return {
          ...series,
          name: series.name.replace(previousDealSeriesName, updatedSeriesName),
          isPrimary: index == 0,
        };
      }
    );

    setFormDealSeries(updatedDealSeriesWithNewNames);

    if (updatedSeriesName === "Other") {
      setIsDealSeriesCustomName(true);
    } else {
      setIsDealSeriesCustomName(false);
    }
  };

  const handleCustomDealRoundNameChange = (e) => {
    const updatedRoundName: string = e.target.value;

    //update the deal series name
    setFormDeal({
      ...formDeal,
      seriesOfPreferredStockSold: updatedRoundName,
    });

    //update the deal series name in the deal series array
    const previousDealSeriesName = formDeal.seriesOfPreferredStockSold;

    //update the deal series name in the deal series array
    const updatedDealSeries = [...formDealSeries];

    const updatedDealSeriesWithNewNames = updatedDealSeries.map((series) => {
      return {
        ...series,
        name: series.name.replace(previousDealSeriesName, updatedRoundName),
      };
    });

    setFormDealSeries(updatedDealSeriesWithNewNames);
  };

  const handleAddSeries = () => {
    const updatedFormDealSeries: api.DealSeries[] = [
      ...formDealSeries,
      {
        id: undefined,
        createdAt: undefined,
        updatedAt: undefined,
        organizationId: undefined,
        dealId: undefined,
        name: formDealSeries[0].name,
        pricePerShare: formDealSeries[0].pricePerShare,
        isPrimary: false,
      },
    ];

    const updatedFormDealSeriesWithSuffix: api.DealSeries[] =
      updatedFormDealSeries.map((series, index) => {
        const seriesNamePrefix = series.name.split("-")[0];

        return { ...series, name: seriesNamePrefix + `-${index + 1}` };
      });

    setFormDealSeries(updatedFormDealSeriesWithSuffix);
    setIsSafeConversion(updatedFormDealSeriesWithSuffix.length > 1);
  };

  const handleRemoveSeries = (index: number) => {
    const updatedFormDealSeries = [...formDealSeries];
    updatedFormDealSeries.splice(index, 1);
    setFormDealSeries(updatedFormDealSeries);
    setIsSafeConversion(updatedFormDealSeries.length > 1);
  };

  const handleIsSafeConversionChange = (value: boolean) => {
    setIsSafeConversion(value);

    if (value === true && formDealSeries.length === 1) {
      handleAddSeries();
    }
  };

  const handleDealSeriesNameChange = (index: number, value: string) => {
    //check if the updated value is a duplicate of an existing series name
    const isDuplicate =
      formDealSeries.filter((dealSeries) => dealSeries.name === value).length >
      0;

    if (isDuplicate) {
      //if duplicate, set error message
      setFormDealSeriesErrorIndex(index);
    } else {
      //otherwise clear error message and update the deal series name
      setFormDealSeriesErrorIndex(null);

      const updatedDealSeries = [...formDealSeries];
      updatedDealSeries[index].name = value;
      updatedDealSeries[index].isPrimary = index == 0;
      setFormDealSeries(updatedDealSeries);
    }
  };

  const handleDealSeriesPricePerShareChange = (
    index: number,
    value: number
  ) => {
    const updatedDealSeries = [...formDealSeries];
    updatedDealSeries[index].pricePerShare = value;
    updatedDealSeries[index].isPrimary = index == 0;
    setFormDealSeries(updatedDealSeries);
  };

  return (
    <div>
      {formDeal && (
        <div className="form-field-container">
          <div className="form-width-container">
            <div className="input-row-container">
              <DKFormTextInput
                dataTestId="round-select"
                label="What round are you raising?"
                name="series_of_preferred_stock_sold"
                select
                fullWidth={true}
                onChange={handleDealSeriesChange}
                value={
                  baseSeriesNames.includes(formDeal.seriesOfPreferredStockSold)
                    ? formDeal.seriesOfPreferredStockSold
                    : "Other"
                }
                SelectProps={{
                  native: true,
                }}
                variant="outlined"
              >
                {[...baseSeriesNames, "Other"].map((option) => {
                  return (
                    <option key={"key_" + option} value={option}>
                      {option}
                    </option>
                  );
                })}
              </DKFormTextInput>
              {isDealSeriesCustomName && (
                <div className="input-row-container">
                  <DKFormTextInput
                    dataTestId="round-name-input"
                    className="series-name-input"
                    required={isDealSeriesCustomName}
                    label="What is the name of the round?"
                    name="series_of_preferred_stock_sold"
                    onChange={handleCustomDealRoundNameChange}
                    value={
                      formDeal.seriesOfPreferredStockSold === "Other"
                        ? undefined
                        : formDeal.seriesOfPreferredStockSold
                    }
                  />
                </div>
              )}
            </div>
            <div className="input-row-container">
              <DKFormMoneyInput
                dataTestId="round-size-input"
                name={"totalRoundSize"}
                label={
                  "What is the total amount of new cash investment in this round?"
                }
                required
                value={formDeal.totalRoundSize}
                onChange={(value) => {
                  handleNumberInputChange("totalRoundSize", value);
                }}
              />
            </div>
            <div className="input-row-container">
              <DKFormMoneyInput
                dataTestId="valuation-input"
                name={"valuation"}
                label={"What is the Pre-Money valuation?"}
                required
                value={formDeal.valuation}
                onChange={(value) => {
                  handleNumberInputChange("valuation", value);
                }}
              />
            </div>
            <div className="input-row-container">
              <DKFormToggleButtonGroup
                label="Will there also be SAFEs converting in this round?"
                items={[
                  { value: "false", label: "No" },
                  { value: "true", label: "Yes" },
                ]}
                selectedValue={isSafeConversion.toString()}
                onChange={(value) => {
                  handleIsSafeConversionChange(value === "true");
                }}
              />
            </div>
            <div className="input-row-label">
              Price Per Share for each Series being issued in the round
            </div>
            {formDealSeries.map((series, index) => {
              return (
                <div key={index}>
                  <div className="form-price-per-share-row-container">
                    <DKFormTextInput
                      dataTestId="series-name-input"
                      className="series-name-input"
                      required
                      label="Series Name"
                      customErrorLabel={
                        formDealSeriesErrorIndex === index
                          ? "Must be unique"
                          : ""
                      }
                      name="name"
                      onChange={(e) => {
                        handleDealSeriesNameChange(index, e.target.value);
                      }}
                      value={series.name}
                    />
                    <DKFormMoneyInput
                      dataTestId="series-price-per-share-input"
                      name={"pricePerShare"}
                      label={"Series Price Per Share"}
                      disabled={
                        !isPricePerShareUpdatable(
                          series,
                          dealLegalEntityInvestments
                        )
                      }
                      required
                      maxDecimalPlaces={9}
                      value={series.pricePerShare}
                      onChange={(value) => {
                        //update the deal series price per share in the deal series array
                        handleDealSeriesPricePerShareChange(index, value);
                      }}
                    />
                    <div className="series-action-container">
                      {isSeriesDeletable(
                        series,
                        dealLegalEntityInvestments
                      ) && (
                        <div
                          className="button-container"
                          data-testid="series-action-remove"
                          onClick={() => handleRemoveSeries(index)}
                        >
                          <RemoveCircleOutlineIcon className="button" />
                        </div>
                      )}
                      {!isSeriesDeletable(
                        series,
                        dealLegalEntityInvestments
                      ) && (
                        <div
                          className="button-container"
                          data-testid="series-action-remove-disabled"
                        >
                          <RemoveCircleOutlineIcon
                            htmlColor={theme.palette.tertiary.dark}
                          />
                        </div>
                      )}
                      {index === formDealSeries.length - 1 && (
                        <div
                          className="button-container"
                          data-testid="series-action-add"
                          onClick={handleAddSeries}
                        >
                          <AddCircleOutlineIcon className="button" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DealInformationForm;
